import { Select, TextInput } from '@octano/global-ui';
import React, { useEffect, useMemo } from 'react';
import { Row, Col } from 'reactstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useClassroomLoader } from './ClassroomLoader';

interface Props {
  preselectId: string;
  preselectName: string;
}

/**
 * Este componente se encarga de mostrar la lista de salones disponibles en la
 * creación o actualización de una nueva clase de una sección (sections_schedules)
 * El listado de clases viene del loader que lo envuelve
 */
function ClassroomSelector({ preselectId, preselectName }: Props) {
  const { control, watch, setValue, getValues } = useFormContext<any>();
  const { data } = useClassroomLoader();
  const code = watch('classroom');
  const { t } = useTranslation();

  const optionsData = useMemo(() => {
    if (!data) {
      return [];
    }
    if (preselectId && !data.some((c: any) => c.id === preselectId)) {
      return [
        {
          id: preselectId,
          name: preselectName,
          code: undefined,
          _synthetic: true,
        },
        ...data,
      ];
    }
    return data;
  }, [data, preselectId, preselectName]);

  useEffect(() => {
    if (!preselectId) {
      return;
    }
    if (getValues('classroom')) {
      return;
    }
    if (!optionsData.length) {
      return;
    }
    const match = optionsData.find((c: any) => c.id === preselectId);
    if (match) {
      setValue('classroom', {
        value: match.id,
        label: match.name,
        code: match.code,
      });
      setValue('classroomCode', match.code || '');
    }
  }, [preselectId, optionsData, setValue, getValues]);

  return (
    <Row>
      <Col>
        <Select
          label={t('classesHistory.classroom')}
          name="classroom"
          options={(optionsData || []).map((classroom: any) => ({
            value: classroom.id,
            label: classroom.name,
            code: classroom.code,
          }))}
          control={control}
          shouldUnregister
        />
      </Col>
      <Col>
        <TextInput
          label={t('classesHistory.classroomCode')}
          name="classroomCode"
          value={code?.code ?? code?.value}
          disabled
        />
      </Col>
    </Row>
  );
}

export default ClassroomSelector;
